import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { gbp } from "@/components/whatsapp/WaCostsTab";
import { Counts } from "@/components/whatsapp/WaSegmentsTab";
import { WaCampaignAnalytics } from "@/components/whatsapp/WaCampaignAnalytics";

const EMPTY = { name: "", segment_id: "", template_name: "", template_language: "en_GB", category: "marketing", budget: "", scheduled_for: "", notes: "" };
const TONE = { draft: "bg-slate-100 text-slate-600", pending_approval: "bg-amber-50 text-amber-700", approved: "bg-emerald-50 text-emerald-700", rejected: "bg-rose-50 text-rose-700", cancelled: "bg-slate-100 text-slate-400" };
const err = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Request failed"; };

function CampaignRow({ c, segName, onChanged, simulator }) {
  const { can, user } = useAuth();
  const [open, setOpen] = useState(false);
  const act = async (path, body) => { try { await api.post(`/wa/campaigns/${c.id}/${path}`, body); toast.success("Updated"); onChanged(); } catch (e) { toast.error(err(e)); } };
  const mine = [c.created_by, c.submitted_by].includes(user?.email);
  return (
    <div className="px-5 py-3" data-testid={`wa-camp-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-slate-800">{c.name}</span>
        <span className={`text-[11px] rounded-full px-2 py-0.5 font-medium ${TONE[c.status]}`} data-testid={`wa-camp-status-${c.id}`}>{c.status.replace("_", " ")}</span>
        <span className="text-xs text-slate-500">{segName} · template {c.template_name} · budget {gbp(c.budget)}{c.scheduled_for ? ` · ${new Date(c.scheduled_for).toLocaleString("en-GB")}` : ""}</span>
      </div>
      {c.estimate && <div className="mt-1 text-xs text-slate-600" data-testid={`wa-camp-estimate-${c.id}`}>Estimated {gbp(c.estimate.total.ex_vat)} ex VAT ({gbp(c.estimate.total.inc_vat)} inc VAT) for {c.estimate.lines[0]?.count || 0} recipients</div>}
      {c.audience_counts && <Counts c={c.audience_counts} testid={`wa-camp-counts-${c.id}`} />}
      {c.status === "approved" && <div className="mt-1 text-xs text-amber-700">Approved · sending is disabled until the Meta live integration is approved.</div>}
      <div className="flex flex-wrap gap-2 mt-2">
        {["draft", "rejected"].includes(c.status) && can("whatsapp.campaigns.manage") && <Button size="sm" variant="outline" onClick={() => act("submit")} data-testid={`wa-camp-submit-${c.id}`}>Submit for approval</Button>}
        {c.status === "pending_approval" && can("whatsapp.campaigns.approve") && !mine && <>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act("approve", { note: null })} data-testid={`wa-camp-approve-${c.id}`}>Approve</Button>
          <Button size="sm" variant="outline" onClick={() => act("reject", { note: window.prompt("Reason for rejecting?") || null })} data-testid={`wa-camp-reject-${c.id}`}>Reject</Button>
        </>}
        {c.status === "pending_approval" && mine && <span className="text-xs text-slate-400">Waiting for another approver</span>}
        {c.status !== "cancelled" && <Button size="sm" variant="ghost" onClick={() => window.confirm("Cancel this campaign?") && act("cancel")} data-testid={`wa-camp-cancel-${c.id}`}>Cancel</Button>}
        <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid={`wa-camp-analytics-${c.id}`}>{open ? "Hide analytics" : "Analytics & links"}</Button>
      </div>
      {open && <WaCampaignAnalytics c={c} simulator={simulator} />}
    </div>
  );
}

export function WaCampaignsTab({ simulator = false }) {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [segs, setSegs] = useState([]);
  const [f, setF] = useState(EMPTY);
  const load = () => {
    api.get("/wa/campaigns").then((r) => setItems(r.data.items));
    api.get("/wa/segments").then((r) => setSegs(r.data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/wa/campaigns", { ...f, budget: Number(f.budget), scheduled_for: f.scheduled_for ? new Date(f.scheduled_for).toISOString() : null }); toast.success("Draft saved"); setF(EMPTY); load(); }
    catch (e) { toast.error(err(e)); }
  };
  const seg = segs.find((s) => s.id === f.segment_id);
  if (!items) return null;
  return (
    <div className="space-y-5">
      {can("whatsapp.campaigns.manage") && (
        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="wa-camp-form">
          <h2 className="text-sm font-bold text-slate-900 mb-3">New campaign draft</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input placeholder="Campaign name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="wa-camp-name" />
            <select value={f.segment_id} onChange={(e) => setF({ ...f, segment_id: e.target.value })} className="rounded-md border border-slate-200 px-2 py-2 text-sm" data-testid="wa-camp-segment">
              <option value="">Choose customer list…</option>
              {segs.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.counts.eligible} eligible)</option>)}
            </select>
            <Input placeholder="Meta template name, e.g. spring_offer_2027" value={f.template_name} onChange={(e) => setF({ ...f, template_name: e.target.value })} data-testid="wa-camp-template" />
            <Input placeholder="Language" value={f.template_language} onChange={(e) => setF({ ...f, template_language: e.target.value })} data-testid="wa-camp-language" />
            <Input type="number" min="0" step="0.01" placeholder="Budget £ (ex VAT)" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} data-testid="wa-camp-budget" />
            <Input type="datetime-local" value={f.scheduled_for} onChange={(e) => setF({ ...f, scheduled_for: e.target.value })} data-testid="wa-camp-schedule" />
          </div>
          {seg && <div className="mt-2"><Counts c={seg.counts} testid="wa-camp-seg-counts" /></div>}
          <Button className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={!f.name || !f.segment_id || !f.template_name || !f.budget} onClick={create} data-testid="wa-camp-create">Save draft</Button>
        </section>
      )}
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm divide-y divide-slate-100" data-testid="wa-camp-list">
        {items.length === 0 ? <div className="p-5 text-sm text-slate-400">No campaigns yet.</div>
          : items.map((c) => <CampaignRow key={c.id} c={c} segName={segs.find((s) => s.id === c.segment_id)?.name || "List"} onChanged={load} simulator={simulator} />)}
      </section>
    </div>
  );
}
